import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";

const BLOGS = "/api/admin/blogs";

const root = ["blogs"];

// Upload answers a path on the API host (/api/blogs/media/x.png). Prefixed for
// previews, and stored prefixed inside article HTML, which the website renders
// from its own origin.
export const mediaUrl = (path) =>
  !path || path.startsWith("http") ? path : `${process.env.REACT_APP_API_BASE_URL || ""}${path}`;

export const STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

// Envelope is the standard Ginox page: { data, currentPage, totalPages, totalRows }.
const fetchBlogList = (f) =>
  axiosInstance
    .get(BLOGS, {
      params: {
        page: f.page,
        pageSize: f.pageSize,
        ...(f.search?.trim() ? { search: f.search.trim() } : {}),
        ...(f.category ? { category: f.category } : {}),
        ...(f.tag ? { tag: f.tag } : {}),
        ...(f.status ? { status: f.status } : {}),
        ...(f.isFeatured ? { isFeatured: f.isFeatured } : {}),
      },
    })
    .then((r) => r.data.data);

export function useBlogList(filters) {
  return useQuery({
    queryKey: [...root, "list", filters],
    queryFn: () => fetchBlogList(filters),
  });
}

// { categories: [{ category, total, published }], tags: [{ tag, count }] }
export function useBlogTaxonomy() {
  return useQuery({
    queryKey: [...root, "taxonomy"],
    queryFn: () => axiosInstance.get(`${BLOGS}/categories`).then((r) => r.data.data),
  });
}

export function useBlog(id) {
  return useQuery({
    queryKey: [...root, "one", id],
    queryFn: () => axiosInstance.get(`${BLOGS}/${id}`).then((r) => r.data.data),
    enabled: !!id,
  });
}

const useBlogMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: root }),
  });
};

// No id creates, an id edits. Both take the same body.
export const useSaveBlog = () =>
  useBlogMutation(({ id, ...body }) =>
    axiosInstance.post(id ? `${BLOGS}/${id}/edit` : BLOGS, body),
  );

export const useDeleteBlog = () =>
  useBlogMutation((id) => axiosInstance.post(`${BLOGS}/${id}/delete`));

export const useBlogStatus = () =>
  useBlogMutation(({ id, status }) => axiosInstance.post(`${BLOGS}/${id}/status`, { status }));

// Multipart, field "image". JPG/PNG/WEBP/GIF up to 10 MB. Returns the stored path.
export const uploadBlogImage = (file) => {
  const body = new FormData();
  body.append("image", file);
  return axiosInstance
    .post(`${BLOGS}/upload-image`, body, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data.data.url);
};

export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
